import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Alert,
  Chip,
  Grid,
  Card,
  CardContent,
  MenuItem,
  Divider,
  FormControl,
  InputLabel,
  Select,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  Stack,
  Slide,
  CircularProgress
} from '@mui/material';
import {
  Add,
  Delete,
  Restaurant,
  Close,
  Warning,
  TrendingUp,
  Category,
  Inventory2,
  Visibility,
  VisibilityOff
} from '@mui/icons-material';

import inventoryService from '../../services/inventoryService';
import AlertDialog from '../../common/AlertDialog';
import useAlertDialog from '../../hooks/useAlertDialog';
import apiClient from '../../../../../../api/client';

// Transition component for smooth dialog animation
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const RecipeForm = ({ 
  open, 
  onClose, 
  onSave, 
  menuItem, 
  recipe, 
  ingredients = [] 
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.down('lg'));
  
  // Alert dialog hook
  const {
    alertState,
    showSuccess,
    showError,
    closeAlert
  } = useAlertDialog();

  const [recipeIngredients, setRecipeIngredients] = useState([]);
  const [yieldQuantity, setYieldQuantity] = useState(1);
  const [yieldUnit, setYieldUnit] = useState('servings');
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [existingRecipeConsumption, setExistingRecipeConsumption] = useState([]);
  
  // NEW: Visualization states (not saved to database)
  // const [visualizationYield, setVisualizationYield] = useState(1);
  // const [showVisualization, setShowVisualization] = useState(false);
  
  // Ingredient filtering state
  const [ingredientFilter, setIngredientFilter] = useState('raw');
  const [currentTab, setCurrentTab] = useState(0);

  const safeParseFloat = (value, fallback = 0) => {
    if (value === '' || value === null || value === undefined) return fallback;
    const parsed = parseFloat(value);
    return !isNaN(parsed) && isFinite(parsed) ? parsed : fallback;
  };

  // Memoize formatQuantity to prevent dependency changes
  const formatQuantity = useCallback((value) => {
    const numValue = safeParseFloat(value);
    if (numValue === 0) return '';
    if (Number.isInteger(numValue)) return numValue.toString();
    return numValue.toString();
  }, []);

  // Keep currency formatting for costs/prices
  const formatCurrency = (value, decimals = 2) => {
    const numValue = safeParseFloat(value);
    return numValue.toFixed(decimals);
  };

  // FIXED: Fetch existing recipe consumption from database
  const fetchExistingRecipeConsumption = useCallback(async () => {
    try {
      console.log('🔄 Loading existing recipe consumption...');
      const response = await apiClient.get('/api/v1/inventory/recipe-ingredient-consumption');
      
      if (response?.data?.success) {
        const consumptionData = response.data.data || [];
        console.log('✅ Loaded recipe consumption data:', consumptionData.length, 'records');
        setExistingRecipeConsumption(consumptionData);
      } else {
        console.warn('⚠️ Recipe consumption data not available');
        setExistingRecipeConsumption([]);
      }
    } catch (err) {
      console.error('❌ Error loading recipe consumption:', err);
      setExistingRecipeConsumption([]);
    }
  }, []);

  // Calculate existing consumption for an ingredient (excluding current recipe if editing)
  const getExistingConsumption = useCallback((ingredientId) => {
    if (!ingredientId) return 0;
    
    const consumptionRecords = existingRecipeConsumption
      .filter(consumption => 
        consumption.ingredient_id === ingredientId && 
        (!recipe || consumption.recipe_id !== recipe.recipe_id)
      );
      
    const totalConsumption = consumptionRecords.reduce(
      (total, consumption) => total + parseFloat(consumption.quantity_per_yield || 0), 
      0
    );
    
    if (consumptionRecords.length > 0 && totalConsumption > 0) {
      console.log(`📊 Ingredient ${ingredientId} has ${consumptionRecords.length} consumption records, total: ${totalConsumption}`);
    }
    
    return totalConsumption;
  }, [existingRecipeConsumption, recipe]);

  // FIXED: Real-time stock calculation that updates immediately when quantities change
  const calculateRealTimeStock = useCallback((ingredient, currentQuantityUsed = 0) => {
    if (!ingredient) return { 
      currentStockInDb: 0, 
      existingConsumption: 0, 
      available: 0, 
      remaining: 0,
      insufficient: false,
      consumedPercentage: 0
    };

    const currentStockInDb = safeParseFloat(ingredient.current_stock);
    
    // Calculate existing consumption from other recipes (excluding current recipe if editing)
    const existingConsumption = getExistingConsumption(ingredient.ingredient_id);
    const newQuantity = safeParseFloat(currentQuantityUsed);
    
    // Available stock = DB stock - existing consumption from other recipes
    const availableBeforeThisRecipe = Math.max(0, currentStockInDb - existingConsumption);
    
    // Remaining after this recipe's usage
    const remainingAfterThisRecipe = Math.max(0, availableBeforeThisRecipe - newQuantity);
    
    // Calculate what percentage of available stock would be consumed
    const consumedPercentage = availableBeforeThisRecipe > 0 
      ? (newQuantity / availableBeforeThisRecipe) * 100 
      : (newQuantity > 0 ? 100 : 0);

    return {
      currentStockInDb,
      existingConsumption,
      available: availableBeforeThisRecipe,
      remaining: remainingAfterThisRecipe,
      insufficient: newQuantity > 0 && availableBeforeThisRecipe < newQuantity,
      consumedPercentage
    };
  }, [getExistingConsumption]);

  // FIXED: Initialize form data with proper recipe loading
  useEffect(() => {
    if (open) {
      // Always fetch existing consumption when dialog opens
      fetchExistingRecipeConsumption();
    }

    if (recipe && open) {
      console.log('🔄 Initializing recipe form with recipe data:', recipe);
      
      // FIXED: Map recipe ingredients with proper ingredient data
      let mappedIngredients = [];
      
      if (recipe.ingredients && Array.isArray(recipe.ingredients)) {
        mappedIngredients = recipe.ingredients.map(recipeIngredient => {
          // Find the full ingredient data
          const ingredient = ingredients.find(ing => 
            ing.ingredient_id === recipeIngredient.ingredient_id || 
            ing.ingredient_id === recipeIngredient.mapping_id
          );
          
          return {
            ingredient_id: recipeIngredient.ingredient_id,
            quantity_per_yield: formatQuantity(recipeIngredient.quantity_per_yield || recipeIngredient.quantity),
            unit: ingredient?.unit_of_measure || ingredient?.package_uom || recipeIngredient.unit || '',
            notes: recipeIngredient.notes || '',
            cost_per_unit: safeParseFloat(ingredient?.cost_per_unit || recipeIngredient.cost_per_unit)
          };
        });
        
        console.log('✅ Mapped recipe ingredients:', mappedIngredients);
      } else {
        console.log('⚠️ No ingredients found in recipe data');
      }
      
      setRecipeIngredients(mappedIngredients);
      setYieldQuantity(safeParseFloat(recipe.yield_quantity, 1));
      setYieldUnit(recipe.yield_unit || 'servings');
      
      // Set visualization yield to current yield for editing
      // setVisualizationYield(safeParseFloat(recipe.yield_quantity, 1));
      
      // Determine correct ingredient type tab from existing recipe ingredients
      if (mappedIngredients.length > 0) {
        const usedIngredientTypes = mappedIngredients
          .map(ri => ingredients.find(ing => ing.ingredient_id === ri.ingredient_id))
          .filter(ing => ing)
          .map(ing => ing.type);
        
        const hasRaw = usedIngredientTypes.includes('raw');
        const hasPremade = usedIngredientTypes.includes('premade');
        
        if (hasRaw) {
          setCurrentTab(0);
          setIngredientFilter('raw');
        } else if (hasPremade) {
          setCurrentTab(1); 
          setIngredientFilter('premade');
        }
        
        console.log('🏷️ Setting tab based on ingredients:', { hasRaw, hasPremade, selectedTab: hasRaw ? 0 : 1 });
      }
      
    } else if (open) {
      // Reset for new recipe
      console.log('🆕 Initializing new recipe form');
      setRecipeIngredients([]);
      setYieldQuantity(1);
      setYieldUnit('servings');
      // setVisualizationYield(1);
      setCurrentTab(0);
      setIngredientFilter('raw');
    }
    setErrors({});
  }, [recipe, open, ingredients, formatQuantity, fetchExistingRecipeConsumption]);

  // Get ingredient cost safely
  const getIngredientCost = useCallback((ingredient) => {
    if (!ingredient) return 0;
    
    const costPerUnit = safeParseFloat(ingredient.cost_per_unit);
    const packageCost = safeParseFloat(ingredient.package_cost);
    const packageSize = safeParseFloat(ingredient.package_size, 1);
    
    return costPerUnit > 0 ? costPerUnit : (packageCost / packageSize);
  }, []);

  // Add ingredient to recipe
  const handleAddIngredient = () => {
    setRecipeIngredients(prev => [...prev, {
      ingredient_id: '',
      quantity_per_yield: '',
      unit: '',
      notes: ''
    }]);
  };

  // Remove ingredient from recipe
  const handleRemoveIngredient = (index) => {
    setRecipeIngredients(prev => prev.filter((_, i) => i !== index));
  };

  // FIXED: Update ingredient in recipe with real-time stock calculation
  const handleIngredientChange = (index, field, value) => {
    setRecipeIngredients(prev => prev.map((ingredient, i) => 
      i === index ? { ...ingredient, [field]: value } : ingredient
    ));
    
    // Force re-render to update real-time stock calculations
    // The stock calculations will update automatically due to the dependency on recipeIngredients
  };

  // Handle yield quantity changes with validation
  const handleYieldQuantityChange = (e) => {
    const value = e.target.value;
    
    if (value === '') {
      setYieldQuantity('');
      return;
    }
    
    const numValue = parseFloat(value);
    if (!isNaN(numValue) && numValue > 0) {
      setYieldQuantity(numValue);
    }
  };

  // Handle yield quantity blur - ensure minimum value
  const handleYieldQuantityBlur = () => {
    if (yieldQuantity === '' || yieldQuantity <= 0) {
      setYieldQuantity(1);
    }
  };

  // Handle tab change for ingredient filtering
  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
    setIngredientFilter(newValue === 0 ? 'raw' : 'premade');
  };

  // Calculate base ingredient cost (for 1 serving)
  const calculateBaseIngredientCost = useCallback(() => {
    return recipeIngredients.reduce((total, recipeIngredient) => {
      const ingredient = ingredients.find(ing => ing.ingredient_id === recipeIngredient.ingredient_id);
      if (!ingredient) return total;
      
      const unitCost = getIngredientCost(ingredient);
      const quantity = safeParseFloat(recipeIngredient.quantity_per_yield);
      
      return total + (unitCost * quantity);
    }, 0);
  }, [recipeIngredients, ingredients, getIngredientCost]);

  // Calculate total cost (base cost × yield quantity)
  const getTotalCost = useCallback(() => {
    const baseCost = calculateBaseIngredientCost();
    const yieldQty = safeParseFloat(yieldQuantity, 1);
    return baseCost * yieldQty;
  }, [calculateBaseIngredientCost, yieldQuantity]);

  // Cost per serving (same as base cost)
  const getCostPerServing = useCallback(() => {
    return calculateBaseIngredientCost();
  }, [calculateBaseIngredientCost]);

  // FIXED: Calculate profit per serving with better price field detection
  const getProfitPerServing = useCallback(() => {
    // FIXED: Try multiple possible field names for selling price
    const sellingPrice = safeParseFloat(
      menuItem?.price || 
      menuItem?.selling_price || 
      menuItem?.menu_price ||
      menuItem?.item_price ||
      0
    );
    
    const costPerServing = getCostPerServing();
    return sellingPrice - costPerServing;
  }, [menuItem, getCostPerServing]);

  // Calculate profit margin percentage
  const getProfitMarginPercentage = useCallback(() => {
    // FIXED: Try multiple possible field names for selling price
    const sellingPrice = safeParseFloat(
      menuItem?.price || 
      menuItem?.selling_price || 
      menuItem?.menu_price ||
      menuItem?.item_price ||
      0
    );
    
    const profitPerServing = getProfitPerServing();
    return sellingPrice > 0 ? (profitPerServing / sellingPrice) * 100 : 0;
  }, [menuItem, getProfitPerServing]);

  // // NEW: Calculate visualization totals for custom yield amount
  // const getVisualizationTotals = useCallback(() => {
  //   const visualYield = safeParseFloat(visualizationYield, 1);
  //   const costPerServing = getCostPerServing();
  //   const sellingPrice = safeParseFloat(
  //     menuItem?.price || 
  //     menuItem?.selling_price || 
  //     menuItem?.menu_price ||
  //     menuItem?.item_price ||
  //     0
  //   );
  //   const profitPerServing = getProfitPerServing();
    
  //   return {
  //     totalCost: costPerServing * visualYield,
  //     totalRevenue: sellingPrice * visualYield,
  //     totalProfit: profitPerServing * visualYield,
  //     costPerServing: costPerServing,
  //     profitPerServing: profitPerServing,
  //     sellingPrice: sellingPrice,
  //     visualYield: visualYield
  //   };
  // }, [visualizationYield, getCostPerServing, menuItem, getProfitPerServing]);

  // FIXED: Calculate totals for entire yield with proper price detection
  const getYieldTotals = useCallback(() => {
    const yieldQty = safeParseFloat(yieldQuantity, 1);
    const totalCost = getTotalCost();
    
    // FIXED: Try multiple possible field names for selling price
    const sellingPrice = safeParseFloat(
      menuItem?.price || 
      menuItem?.selling_price || 
      menuItem?.menu_price ||
      menuItem?.item_price ||
      0
    );
    
    const costPerServing = getCostPerServing();
    const profitPerServing = getProfitPerServing();
    
    return {
      totalCost: totalCost,
      totalSellingPrice: sellingPrice * yieldQty,
      totalProfitMargin: profitPerServing * yieldQty,
      costPerServing: costPerServing,
      profitPerServing: profitPerServing,
      sellingPrice: sellingPrice // Add this for display
    };
  }, [yieldQuantity, getTotalCost, menuItem, getCostPerServing, getProfitPerServing]);

  // Enhanced stock warnings with real-time consumption
  const getStockWarnings = useCallback(() => {
    const warnings = [];
    recipeIngredients.forEach((recipeIngredient, index) => {
      const ingredient = ingredients.find(ing => ing.ingredient_id === recipeIngredient.ingredient_id);
      if (!ingredient) return;
      
      const requiredQuantity = safeParseFloat(recipeIngredient.quantity_per_yield);
      const stockInfo = calculateRealTimeStock(ingredient, requiredQuantity);
      const unit = ingredient.unit_of_measure || ingredient.package_uom || '';
      
      if (stockInfo.insufficient) {
        warnings.push({
          index,
          ingredientName: ingredient.name,
          required: requiredQuantity,
          available: stockInfo.available,
          remaining: stockInfo.remaining,
          unit,
          consumedPercentage: stockInfo.consumedPercentage
        });
      }
    });
    return warnings;
  }, [recipeIngredients, ingredients, calculateRealTimeStock]);

  // Filter ingredients based on current tab selection
  const getFilteredIngredients = useCallback(() => {
    return ingredients.filter(ingredient => {
      if (!ingredient.name || !ingredient.ingredient_id) return false;
      return ingredient.type === ingredientFilter;
    });
  }, [ingredients, ingredientFilter]);

  // Get ingredient category label
  const getIngredientCategoryLabel = (type) => {
    switch (type) {
      case 'raw':
        return t('inventory.filters.raw');
      case 'premade':
        return t('inventory.filters.premade');
      default:
        return t('inventory.filters.uncategorized');
    }
  };

  // Get ingredient category chip color
  const getIngredientCategoryColor = (type) => {
    switch (type) {
      case 'raw':
        return 'info';
      case 'premade':
        return 'success';
      default:
        return 'default';
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (recipeIngredients.length === 0) {
      newErrors.ingredients = t('ingredients.validation.ingredientRequired');
    }
    
    if (!yieldQuantity || yieldQuantity <= 0) {
      newErrors.yieldQuantity = t('ingredients.validation.quantityRequired');
    }
    
    recipeIngredients.forEach((ingredient, index) => {
      if (!ingredient.ingredient_id) {
        newErrors[`ingredient_${index}`] = t('ingredients.validation.ingredientRequired');
      }
      if (!ingredient.quantity_per_yield || safeParseFloat(ingredient.quantity_per_yield) <= 0) {
        newErrors[`quantity_${index}`] = t('ingredients.validation.quantityRequired');
      }
    });
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // FIXED: Handle save with proper data structure - ALWAYS SAVE AS 1 SERVING
  const handleSave = async () => {
    if (!validateForm()) return;
    
    setLoading(true);
    try {
      // IMPORTANT: Always save yield_quantity as 1 for consistency
      const recipeData = {
        item_id: menuItem.item_id,
        yield_quantity: 1, // Always save as 1 serving regardless of form input
        yield_unit: yieldUnit,
        ingredients: recipeIngredients.map(ing => {
          const ingredient = ingredients.find(i => i.ingredient_id === ing.ingredient_id);
          return {
            ingredient_id: ing.ingredient_id,
            quantity_per_yield: parseFloat(ing.quantity_per_yield),
            unit: ing.unit || ingredient?.unit_of_measure || 'unit',
            notes: ing.notes,
            cost_per_unit: getIngredientCost(ingredient)
          };
        })
      };
      
      console.log('💾 Saving recipe with data (force yield_quantity = 1):', recipeData);
      
      const action = recipe ? 'updated' : 'created';
      
      // Save recipe and wait for response
      const response = await inventoryService.saveRecipe(recipeData);
      
      console.log('✅ Recipe save response:', response);
      
      // Force refresh consumption data immediately to update stock display
      await fetchExistingRecipeConsumption();
      
      // Show success message
      showSuccess(
        t('common.success'),
        t('ingredients.recipe.createSuccess', { name: menuItem.name }) || `Recipe ${action} successfully for ${menuItem.name}`
      );
      
      // Call onSave callback with the response data
      if (typeof onSave === 'function') {
        await onSave(recipeData);
      }
      
      // Close the dialog after a brief delay to show the success message
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch (err) {
      console.error(`❌ Error ${recipe ? 'updating' : 'saving'} recipe:`, err);
      showError(
        t('common.error'),
        err.response?.data?.message || err.message || `Failed to ${recipe ? 'update' : 'create'} recipe`
      );
    } finally {
      setLoading(false);
    }
  };

  const yieldTotals = getYieldTotals();
  // const visualizationTotals = getVisualizationTotals();
  const stockWarnings = getStockWarnings();
  const profitMarginPercentage = getProfitMarginPercentage();
  const filteredIngredients = getFilteredIngredients();

  if (!open) return null;

  return (
    <>
      <Dialog 
        open={open} 
        onClose={onClose} 
        maxWidth="xl" 
        fullWidth
        fullScreen={isMobile}
        TransitionComponent={isMobile ? Transition : undefined}
        PaperProps={{
          sx: {
            ...(isMobile && {
              margin: 0,
              borderRadius: 0,
              height: '100%'
            })
          }
        }}
      >
        {/* FIXED: DialogTitle HTML nesting issue */}
        <DialogTitle sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          gap: isMobile ? 1 : 2,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          py: isMobile ? 1.5 : 2,
          px: isMobile ? 2 : 3
        }}>
          <Restaurant />
          <Box sx={{ flex: 1, fontSize: isMobile ? '1.1rem' : '1.25rem' }}>
            {recipe ? t('common.edit') : t('common.add')} {t('ingredients.recipe.title')} - {menuItem?.name}
          </Box>
          {loading && <CircularProgress size={24} sx={{ color: 'white' }} />}
          <IconButton 
            onClick={onClose} 
            sx={{ color: 'white' }}
          >
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: isMobile ? 2 : 3 }}>
          {/* Basic Information & Recipe Integration */}
          <Card variant="outlined" sx={{ mb: 3, mt: 1 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                {t('ingredients.basicInformation')} & {t('ingredients.recipe.title')}
              </Typography>
              
              <Grid container spacing={isMobile ? 2 : 3}>
                {/* Menu Item Info */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: theme.palette.grey[50], 
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.divider}`
                  }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 600, 
                      mb: 1, 
                      color: theme.palette.primary.main, 
                      fontSize: isMobile ? '0.8rem' : '0.875rem' 
                    }}>
                      📋 {t('ingredients.basicInformation')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, mb: 1, fontSize: isMobile ? '0.9rem' : '1rem' }}>
                      {menuItem?.name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                      {menuItem?.description || 'No description'}
                    </Typography>
                    {/* FIXED: Display selling price with multiple field attempts */}
                    <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                      {t('inventory.setup.sellingPrice')}: ${formatCurrency(
                        menuItem?.price || 
                        menuItem?.selling_price || 
                        menuItem?.menu_price ||
                        menuItem?.item_price ||
                        0
                      )}
                    </Typography>
                  </Box>
                </Grid>

                {/* Recipe Configuration */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: theme.palette.info.lighter || 'rgba(33, 150, 243, 0.1)', 
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.info.main}30`
                  }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 600, 
                      mb: 2, 
                      color: theme.palette.info.main, 
                      fontSize: isMobile ? '0.8rem' : '0.875rem' 
                    }}>
                      🧾 {t('ingredients.recipe.title')} {t('common.configuration')}
                    </Typography>
                    
                    <Grid container spacing={2}>
                      <Grid item xs={8}>
                        <TextField
                          fullWidth
                          label={t('ingredients.recipe.yieldQuantity')}
                          type="number"
                          value={formatQuantity(yieldQuantity)}
                          onChange={handleYieldQuantityChange}
                          onBlur={handleYieldQuantityBlur}
                          onFocus={(e) => e.target.select()}
                          error={!!errors.yieldQuantity}
                          helperText={errors.yieldQuantity || t('ingredients.recipe.yieldQuantityHelp')}
                          inputProps={{ 
                            min: 1, 
                            step: 0.1,
                            onKeyDown: (e) => {
                              if ([8, 9, 27, 13, 46, 190].indexOf(e.keyCode) !== -1 ||
                                  (e.keyCode === 65 && e.ctrlKey === true) ||
                                  (e.keyCode === 67 && e.ctrlKey === true) ||
                                  (e.keyCode === 86 && e.ctrlKey === true) ||
                                  (e.keyCode === 88 && e.ctrlKey === true) ||
                                  (e.keyCode >= 35 && e.keyCode <= 39)) {
                                return;
                              }
                              if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105) && e.keyCode !== 190) {
                                e.preventDefault();
                              }
                            }
                          }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={4}>
                        <TextField
                          fullWidth
                          label={t('ingredients.recipe.yieldUnit')}
                          value={yieldUnit}
                          onChange={(e) => setYieldUnit(e.target.value)}
                          placeholder={t('ingredients.recipe.yieldUnitPlaceholder')}
                          helperText={t('ingredients.recipe.yieldUnitHelp')}
                          size="small"
                        />
                      </Grid>
                    </Grid>

                    {/* NOTE: Yield will always save as 1 */}
                    <Alert severity="info" sx={{ mt: 2, fontSize: '0.75rem' }}>
                      <Typography variant="caption">
                        <strong>Note:</strong> Recipe will be saved with yield_quantity = 1 for consistency.
                        Use the visualization below to analyze different yield amounts.
                      </Typography>
                    </Alert>
                  </Box>
                </Grid>

                {/* Cost Summary */}
                <Grid item xs={12} md={4}>
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: theme.palette.success.lighter || 'rgba(76, 175, 80, 0.1)', 
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.success.main}30`
                  }}>
                    <Typography variant="subtitle2" sx={{ 
                      fontWeight: 600, 
                      mb: 2, 
                      color: theme.palette.success.main, 
                      fontSize: isMobile ? '0.8rem' : '0.875rem' 
                    }}>
                      💰 {t('ingredients.recipe.summary')}
                    </Typography>
                    
                    <Grid container spacing={1}>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('ingredients.recipe.totalCost')}
                        </Typography>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600, 
                          color: theme.palette.success.main, 
                          fontSize: isMobile ? '1rem' : '1.25rem' 
                        }}>
                          ${formatCurrency(yieldTotals.totalCost)}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" color="text.secondary">
                          {t('ingredients.recipe.costPerServing')}
                        </Typography>
                        <Typography variant="h6" sx={{ 
                          fontWeight: 600, 
                          color: theme.palette.info.main, 
                          fontSize: isMobile ? '1rem' : '1.25rem' 
                        }}>
                          ${formatCurrency(yieldTotals.costPerServing)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>

              {/* NEW: Business Visualization Section
              <Box sx={{ mt: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    📊 Business Visualization
                  </Typography>
                  <Button
                    startIcon={showVisualization ? <VisibilityOff /> : <Visibility />}
                    onClick={() => setShowVisualization(!showVisualization)}
                    variant="outlined"
                    size="small"
                  >
                    {showVisualization ? 'Hide' : 'Show'} Profit Analysis
                  </Button>
                </Box>

                {showVisualization && (
                  <Box sx={{ 
                    p: 2, 
                    backgroundColor: theme.palette.warning.lighter || 'rgba(255, 152, 0, 0.1)', 
                    borderRadius: 2,
                    border: `1px solid ${theme.palette.warning.main}30`
                  }}>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          label="Visualization Yield"
                          type="number"
                          value={visualizationYield}
                          onChange={(e) => setVisualizationYield(Math.max(1, parseFloat(e.target.value) || 1))}
                          helperText="Enter serving amount to analyze profit"
                          inputProps={{ min: 1, step: 1 }}
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <Grid container spacing={1}>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'error.lighter', borderRadius: 1 }}>
                              <Typography variant="caption">Total Cost</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: 'error.main' }}>
                                ${formatCurrency(visualizationTotals.totalCost)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'info.lighter', borderRadius: 1 }}>
                              <Typography variant="caption">Total Revenue</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: 'info.main' }}>
                                ${formatCurrency(visualizationTotals.totalRevenue)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={4}>
                            <Box sx={{ textAlign: 'center', p: 1, bgcolor: 'success.lighter', borderRadius: 1 }}>
                              <Typography variant="caption">Total Profit</Typography>
                              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                                ${formatCurrency(visualizationTotals.totalProfit)}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1, textAlign: 'center' }}>
                          Profit Margin: {((visualizationTotals.totalProfit / visualizationTotals.totalRevenue) * 100).toFixed(1)}% 
                          | Per Unit Profit: ${formatCurrency(visualizationTotals.profitPerServing)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box> */}
            </CardContent>
          </Card>

          {/* Enhanced Stock Warnings with real-time consumption details */}
          {stockWarnings.length > 0 && (
            <Alert severity="warning" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, display: 'flex', alignItems: 'center' }}>
                <Warning sx={{ mr: 1 }} />
                {t('ingredients.recipe.stockWarnings')}
              </Typography>
              {stockWarnings.map((warning, index) => (
                <Typography key={index} variant="body2" sx={{ mb: 0.5, fontSize: isMobile ? '0.8rem' : '0.875rem' }}>
                  • {warning.ingredientName}: {t('inventory.needs')} {formatQuantity(warning.required)}{warning.unit}, 
                  {t('inventory.available')} {formatQuantity(warning.available)}{warning.unit}
                  {warning.consumedPercentage > 50 && (
                    <Chip 
                      size="small" 
                      label={`${warning.consumedPercentage.toFixed(0)}% consumed`} 
                      color="warning" 
                      sx={{ ml: 1, fontSize: '0.7rem' }}
                    />
                  )}
                </Typography>
              ))}
            </Alert>
          )}

          {/* Ingredient Type Selection */}
          <Card variant="outlined" sx={{ mb: 3 }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                {t('ingredients.recipe.selectIngredientType')}
              </Typography>
              
              <Tabs
                value={currentTab}
                onChange={handleTabChange}
                variant="fullWidth"
                sx={{
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 600,
                    fontSize: isMobile ? '0.8rem' : '0.9rem'
                  },
                  '& .MuiTabs-indicator': {
                    height: 3,
                    borderRadius: '3px 3px 0 0'
                  }
                }}
              >
                <Tab
                  icon={<Category />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
                      <span>{t('inventory.filters.raw')}</span>
                      <Chip 
                        size="small" 
                        label={ingredients.filter(ing => ing.type === 'raw').length}
                        color="info"
                      />
                    </Box>
                  }
                  iconPosition="start"
                />
                <Tab
                  icon={<Inventory2 />}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexDirection: isMobile ? 'column' : 'row' }}>
                      <span>{t('inventory.filters.premade')}</span>
                      <Chip 
                        size="small" 
                        label={ingredients.filter(ing => ing.type === 'premade').length}
                        color="success"
                      />
                    </Box>
                  }
                  iconPosition="start"
                />
              </Tabs>
            </CardContent>
          </Card>

          {/* Ingredients Table */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              mb: 2,
              flexDirection: isMobile ? 'column' : 'row',
              gap: isMobile ? 1 : 0
            }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                {currentTab === 0 ? t('inventory.filters.raw') : t('inventory.filters.premade')} ({recipeIngredients.length})
              </Typography>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddIngredient}
                size={isMobile ? "medium" : "small"}
                disabled={loading}
                sx={{
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                  '&:hover': {
                    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
                  }
                }}
              >
                {t('common.add')}
              </Button>
            </Box>

            {errors.ingredients && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.ingredients}
              </Alert>
            )}

            {isMobile ? (
              // Mobile Card Layout
              <Stack spacing={2}>
                {recipeIngredients.map((recipeIngredient, index) => {
                  const selectedIngredient = ingredients.find(ing => ing.ingredient_id === recipeIngredient.ingredient_id);
                  const unitCost = getIngredientCost(selectedIngredient);
                  const quantity = safeParseFloat(recipeIngredient.quantity_per_yield);
                  const lineCost = unitCost * quantity;
                  // FIXED: Real-time stock calculation with current quantity
                  const stockInfo = calculateRealTimeStock(selectedIngredient, quantity);
                  const unit = selectedIngredient?.unit_of_measure || selectedIngredient?.package_uom || '';

                  return (
                    <Card key={index} variant="outlined" sx={{ p: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                          {t('ingredients.ingredient')} #{index + 1}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => handleRemoveIngredient(index)}
                          color="error"
                        >
                          <Delete />
                        </IconButton>
                      </Box>
                      
                      <Stack spacing={2}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label={t('ingredients.ingredient')}
                          value={recipeIngredient.ingredient_id || ''}
                          onChange={(e) => {
                            handleIngredientChange(index, 'ingredient_id', e.target.value);
                            const ingredient = ingredients.find(ing => ing.ingredient_id === e.target.value);
                            if (ingredient) {
                              handleIngredientChange(index, 'unit', ingredient.unit_of_measure || ingredient.package_uom || '');
                            }
                          }}
                          error={!!errors[`ingredient_${index}`]}
                          helperText={errors[`ingredient_${index}`]}
                        >
                          <MenuItem value="">
                            <em>{t('inventory.selectIngredient')}</em>
                          </MenuItem>
                          {filteredIngredients.map((ingredient) => (
                            <MenuItem key={ingredient.ingredient_id} value={ingredient.ingredient_id}>
                              <Box sx={{ width: '100%' }}>
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                  {ingredient.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  ${formatCurrency(getIngredientCost(ingredient), 4)} • Stock: {formatQuantity(ingredient.current_stock)}
                                </Typography>
                              </Box>
                            </MenuItem>
                          ))}
                        </TextField>

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label={t('common.quantity')}
                              type="number"
                              value={recipeIngredient.quantity_per_yield}
                              onChange={(e) => handleIngredientChange(index, 'quantity_per_yield', e.target.value)}
                              error={!!errors[`quantity_${index}`]}
                              helperText={errors[`quantity_${index}`]}
                              inputProps={{ min: 0, step: 0.001 }}
                            />
                          </Grid>
                          <Grid item xs={6}>
                            <TextField
                              fullWidth
                              size="small"
                              label={t('common.unit')}
                              value={unit || '-'}
                              disabled
                            />
                          </Grid>
                        </Grid>

                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 1, 
                              backgroundColor: theme.palette.info.lighter || 'rgba(33, 150, 243, 0.1)', 
                              borderRadius: 1 
                            }}>
                              <Typography variant="caption" color="text.secondary">
                                {t('inventory.setup.costPerUnit')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                ${formatCurrency(unitCost, 4)}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 1, 
                              backgroundColor: theme.palette.success.lighter || 'rgba(76, 175, 80, 0.1)', 
                              borderRadius: 1 
                            }}>
                              <Typography variant="caption" color="text.secondary">
                                {t('ingredients.recipe.lineCost')}
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                                ${formatCurrency(lineCost)}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {/* FIXED: Real-time stock display with immediate updates */}
                        <Grid container spacing={2}>
                          <Grid item xs={6}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 1, 
                              backgroundColor: stockInfo.insufficient ? (theme.palette.error.lighter || 'rgba(244, 67, 54, 0.1)') : (theme.palette.success.lighter || 'rgba(76, 175, 80, 0.1)'), 
                              borderRadius: 1 
                            }}>
                              <Typography variant="caption" color="text.secondary">
                                Available Stock
                              </Typography>
                              <Typography variant="body2" sx={{ 
                                fontWeight: 600, 
                                color: stockInfo.insufficient ? theme.palette.error.main : 'text.primary' 
                              }}>
                                {formatQuantity(stockInfo.available)}{unit}
                              </Typography>
                            </Box>
                          </Grid>
                          <Grid item xs={6}>
                            <Box sx={{ 
                              textAlign: 'center', 
                              p: 1, 
                              backgroundColor: stockInfo.remaining < stockInfo.available * 0.2 ? (theme.palette.warning.lighter || 'rgba(255, 152, 0, 0.1)') : (theme.palette.info.lighter || 'rgba(33, 150, 243, 0.1)'), 
                              borderRadius: 1 
                            }}>
                              <Typography variant="caption" color="text.secondary">
                                After This Recipe
                              </Typography>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {formatQuantity(stockInfo.remaining)}{unit}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        {stockInfo.insufficient && (
                          <Alert severity="warning" size="small">
                            <Typography variant="caption">
                              {t('inventory.lowStock')}: {formatQuantity(stockInfo.available)}{unit} {t('inventory.available')}
                            </Typography>
                          </Alert>
                        )}

                        <TextField
                          fullWidth
                          size="small"
                          label={t('common.notes')}
                          value={recipeIngredient.notes}
                          onChange={(e) => handleIngredientChange(index, 'notes', e.target.value)}
                          placeholder={t('common.notes')}
                        />
                      </Stack>
                    </Card>
                  );
                })}
              </Stack>
            ) : (
              // Desktop Table Layout
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                      <TableCell sx={{ fontWeight: 600, minWidth: isTablet ? 150 : 200 }}>
                        {t('ingredients.ingredient')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>
                        {t('ingredients.type')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: isTablet ? 100 : 120 }}>
                        {t('ingredients.recipe.quantityPerYield')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>
                        {t('common.unit')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: isTablet ? 100 : 120 }}>
                        {t('inventory.setup.costPerUnit')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>
                        {t('ingredients.recipe.lineCost')}
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: isTablet ? 100 : 120 }}>
                        Real-time Stock
                      </TableCell>
                      <TableCell sx={{ fontWeight: 600, minWidth: isTablet ? 120 : 150 }}>
                        {t('common.notes')}
                      </TableCell>
                      <TableCell align="center" sx={{ fontWeight: 600, minWidth: 80 }}>
                        {t('common.actions')}
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {recipeIngredients.map((recipeIngredient, index) => {
                      const selectedIngredient = ingredients.find(ing => ing.ingredient_id === recipeIngredient.ingredient_id);
                      
                      const unitCost = getIngredientCost(selectedIngredient);
                      const quantity = safeParseFloat(recipeIngredient.quantity_per_yield);
                      const lineCost = unitCost * quantity;
                      // FIXED: Real-time stock calculation with current quantity
                      const stockInfo = calculateRealTimeStock(selectedIngredient, quantity);
                      const unit = selectedIngredient?.unit_of_measure || selectedIngredient?.package_uom || '';

                      return (
                        <TableRow key={index} sx={{ '&:hover': { backgroundColor: theme.palette.grey[50] } }}>
                          <TableCell>
                            <TextField
                              select
                              fullWidth
                              size="small"
                              value={recipeIngredient.ingredient_id || ''}
                              onChange={(e) => {
                                handleIngredientChange(index, 'ingredient_id', e.target.value);
                                const ingredient = ingredients.find(ing => ing.ingredient_id === e.target.value);
                                if (ingredient) {
                                  handleIngredientChange(index, 'unit', ingredient.unit_of_measure || ingredient.package_uom || '');
                                }
                              }}
                              error={!!errors[`ingredient_${index}`]}
                            >
                              <MenuItem value="">
                                <em>{t('inventory.selectIngredient')}</em>
                              </MenuItem>
                              {filteredIngredients.map((ingredient) => {
                                const ingredientCost = getIngredientCost(ingredient);
                                const ingredientUnit = ingredient.unit_of_measure || ingredient.package_uom || '';
                                
                                return (
                                  <MenuItem key={ingredient.ingredient_id} value={ingredient.ingredient_id}>
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                      <Box>
                                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                          {ingredient.name}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                          {ingredientUnit} • Stock: {formatQuantity(ingredient.current_stock)}
                                        </Typography>
                                      </Box>
                                      <Typography variant="body2" color="primary.main" sx={{ fontWeight: 500 }}>
                                        ${formatCurrency(ingredientCost, 4)}
                                      </Typography>
                                    </Box>
                                  </MenuItem>
                                );
                              })}
                            </TextField>
                            {errors[`ingredient_${index}`] && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {errors[`ingredient_${index}`]}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={getIngredientCategoryLabel(selectedIngredient?.type)}
                              color={getIngredientCategoryColor(selectedIngredient?.type)}
                              sx={{ fontSize: '0.7rem' }}
                            />
                          </TableCell>

                          <TableCell>
                            <TextField
                              size="small"
                              type="number"
                              value={recipeIngredient.quantity_per_yield}
                              onChange={(e) => handleIngredientChange(index, 'quantity_per_yield', e.target.value)}
                              error={!!errors[`quantity_${index}`]}
                              inputProps={{ min: 0, step: 0.001 }}
                              sx={{ width: '100%' }}
                            />
                            {errors[`quantity_${index}`] && (
                              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 0.5 }}>
                                {errors[`quantity_${index}`]}
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2">
                              {unit || '-'}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              ${formatCurrency(unitCost, 4)}
                            </Typography>
                          </TableCell>

                          <TableCell>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                              ${formatCurrency(lineCost)}
                            </Typography>
                          </TableCell>

                          {/* FIXED: Real-time stock display with immediate consumption updates */}
                          <TableCell>
                            <Stack spacing={0.5}>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  fontWeight: 600,
                                  color: stockInfo.insufficient ? theme.palette.error.main : theme.palette.success.main
                                }}
                              >
                                {formatQuantity(stockInfo.available)}{unit}
                              </Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.65rem' }}>
                                → {formatQuantity(stockInfo.remaining)}{unit} after
                              </Typography>
                              {stockInfo.insufficient && (
                                <Chip 
                                  label={t('inventory.lowStock')} 
                                  size="small" 
                                  color="error"
                                  sx={{ fontSize: '0.7rem' }}
                                />
                              )}
                              {quantity > 0 && stockInfo.consumedPercentage > 0 && (
                                <Chip 
                                  size="small" 
                                  label={`${stockInfo.consumedPercentage.toFixed(0)}% used`}
                                  color={stockInfo.consumedPercentage > 80 ? 'error' : 
                                         stockInfo.consumedPercentage > 50 ? 'warning' : 'info'}
                                  sx={{ fontSize: '0.6rem', height: '16px' }}
                                />
                              )}
                            </Stack>
                          </TableCell>

                          <TableCell>
                            <TextField
                              size="small"
                              value={recipeIngredient.notes}
                              onChange={(e) => handleIngredientChange(index, 'notes', e.target.value)}
                              placeholder={t('common.notes')}
                              sx={{ width: '100%' }}
                            />
                          </TableCell>

                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveIngredient(index)}
                              color="error"
                            >
                              <Delete fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Empty State */}
            {recipeIngredients.length === 0 && (
              <Box sx={{ 
                textAlign: 'center', 
                py: 4, 
                border: '1px dashed', 
                borderColor: 'divider', 
                borderRadius: 2 
              }}>
                <Restaurant sx={{ fontSize: 48, color: 'text.secondary', mb: 1 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('ingredients.recipe.noIngredients')}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<Add />}
                  onClick={handleAddIngredient}
                  sx={{ mt: 2 }}
                  disabled={loading}
                >
                  {t('common.add')}
                </Button>
              </Box>
            )}
          </Box>

          {/* FIXED: Profit & Yield Analysis with corrected selling price display */}
          <Card variant="outlined" sx={{ 
            mb: 3, 
            backgroundColor: theme.palette.primary.lighter || 'rgba(25, 118, 210, 0.1)',
            border: `1px solid ${theme.palette.primary.main}30`
          }}>
            <CardContent sx={{ p: isMobile ? 2 : 3 }}>
              <Typography variant="h6" sx={{ 
                fontWeight: 600, 
                mb: 2, 
                display: 'flex', 
                alignItems: 'center', 
                fontSize: isMobile ? '1rem' : '1.25rem' 
              }}>
                <TrendingUp sx={{ mr: 1 }} />
                {t('ingredients.recipe.profitAnalysis')} & {t('inventory.setup.totalYield')} {t('ingredients.recipe.summary')}
              </Typography>
              
              {/* Per Serving Metrics */}
              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 600, 
                  mb: 1, 
                  color: theme.palette.primary.main 
                }}>
                  📊 {t('common.per')} {yieldUnit.slice(0, -1) || 'serving'}:
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('inventory.setup.sellingPrice')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      ${formatCurrency(yieldTotals.sellingPrice)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('ingredients.recipe.costPerServing')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                      ${formatCurrency(yieldTotals.costPerServing)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('ingredients.recipe.profitMargin')}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 600,
                        color: yieldTotals.profitPerServing > 0 ? theme.palette.success.main : theme.palette.error.main
                      }}
                    >
                      ${formatCurrency(yieldTotals.profitPerServing)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('common.percentage')}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 600,
                        color: profitMarginPercentage > 0 ? theme.palette.success.main : theme.palette.error.main
                      }}
                    >
                      {profitMarginPercentage.toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Box>

              <Divider sx={{ my: 1.5 }} />

              {/* Total Yield Metrics */}
              <Box>
                <Typography variant="subtitle2" sx={{ 
                  fontWeight: 600, 
                  mb: 1, 
                  color: theme.palette.info.main 
                }}>
                  🎯 {t('inventory.setup.totalYield')} ({formatQuantity(yieldQuantity)} {yieldUnit}):
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('ingredients.recipe.totalCost')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.warning.main }}>
                      ${formatCurrency(yieldTotals.totalCost)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('inventory.setup.totalValue')} ({t('inventory.setup.sellingPrice')})
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                      ${formatCurrency(yieldTotals.totalSellingPrice)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('ingredients.recipe.totalCost')} {t('ingredients.recipe.profitMargin')}
                    </Typography>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 600,
                        color: yieldTotals.totalProfitMargin > 0 ? theme.palette.success.main : theme.palette.error.main
                      }}
                    >
                      ${formatCurrency(yieldTotals.totalProfitMargin)}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption" color="text.secondary">
                      {t('ingredients.recipe.profitMargin')} %
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: theme.palette.info.main }}>
                      {profitMarginPercentage.toFixed(1)}%
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </CardContent>
          </Card>
        </DialogContent>

        <DialogActions sx={{ px: isMobile ? 2 : 3, pb: isMobile ? 2 : 3, gap: 1 }}>
          <Button 
            onClick={onClose} 
            color="inherit" 
            size={isMobile ? "large" : "medium"}
            disabled={loading}
          >
            {t('common.cancel')}
          </Button>
          <Button 
            onClick={handleSave} 
            variant="contained" 
            disabled={recipeIngredients.length === 0 || loading}
            size={isMobile ? "large" : "medium"}
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              '&:hover': {
                background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`
              },
              ...(isMobile && { flex: 1 })
            }}
          >
            {loading ? <CircularProgress size={20} sx={{ color: 'white', mr: 1 }} /> : null}
            {recipe ? t('common.update') : t('common.save')} {t('ingredients.recipe.title')} (Saves as 1 Serving)
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog
        open={alertState.open}
        onClose={closeAlert}
        type={alertState.type}
        title={alertState.title}
        message={alertState.message}
        confirmText={alertState.confirmText}
        onConfirm={alertState.onConfirm}
        autoClose={alertState.autoClose}
      />
    </>
  );
};

export default RecipeForm;